import React, { useState } from 'react';

import { Input } from 'antd';
import { T } from 'react-translator-component';

import { fieldKind } from './buildPayload';
import { FieldError, FieldGroup, FieldLabel, FullWidthSelect } from './styled';

import { RequestField } from '../../../types/responses/Jira';

const MAX_BYTES = 10 * 1024 * 1024;
const EXTENSIONS = ['png', 'jpg', 'jpeg', 'pdf', 'txt', 'csv', 'log', 'zip'];

interface Props {
  field: RequestField;
  value: any;
  error?: string;
  onChange: (v: any) => void;
  onFile: (file: File) => void;
}

const DynamicField = ({ field, value, error, onChange, onFile }: Props) => {
  const [fileError, setFileError] = useState('');
  const kind = fieldKind(field);
  const id = `ticket-field-${field.fieldId}`;

  const pick = (file?: File) => {
    if (!file) return;
    const ext = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : '';
    if (!EXTENSIONS.includes(ext)) return setFileError(T('File type not allowed'));
    if (file.size > MAX_BYTES) return setFileError(T('File too large (max 10 MB)'));
    setFileError('');
    onFile(file);
  };

  let control: React.ReactNode;
  switch (kind) {
    case 'textarea':
      control = <Input.TextArea id={id} rows={4} value={value || ''} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'datetime':
      control = <Input id={id} type="datetime-local" value={value || ''} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'date':
      control = <Input id={id} type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'number':
      control = <Input id={id} type="number" value={value || ''} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'select':
      control = (
        <FullWidthSelect
          id={id}
          value={value}
          onChange={onChange}
          options={field.validValues.map((o) => ({ value: o.value, label: o.label }))}
        />
      );
      break;
    case 'attachment':
      control = (
        <input
          id={id}
          type="file"
          onChange={(e) => {
            pick(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      );
      break;
    case 'user':
      control = <Input id={id} disabled value={value || ''} />;
      break;
    default:
      control = <Input id={id} value={value || ''} onChange={(e) => onChange(e.target.value)} />;
  }

  return (
    <FieldGroup>
      <FieldLabel htmlFor={id}>
        {field.name}
        {(field.required || kind === 'summary') && ' *'}
      </FieldLabel>
      {control}
      {(error || fileError) && <FieldError>{error ? T(error) : fileError}</FieldError>}
    </FieldGroup>
  );
};

export default DynamicField;
